import {
  FlexBoxImageScaleView,
  FlexBoxView,
} from '@play-co/timestep-core/flexbox';
import uiConfig from 'src/lib/ui/config';
import i18n from 'src/lib/i18n/i18n';
import animate from '@play-co/timestep-core/lib/animate';
import { animDuration } from 'src/lib/utils';
import { ImageView, View } from '@play-co/timestep-core/ui';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import sounds from 'src/lib/sounds';
import ShopActionButton, { ShopActionType } from './ShopActionButton';
import StateObserver, { user } from 'src/StateObserver';
import {
  isBPointsButtonDisabled,
  isGemsButtonDisabled,
  openCoinsShopSequence,
  openEnergyShopSequence,
  openGemsShopSequence,
} from '../shop/Helpers';
import { GachaShopBanner } from '../decoration/GachaShopBanner';
import { getCurrentGachaEvent } from 'src/replicant/gacha/gacha.getters';
import { isDecorationGachaEventAvailable } from '../decoration/helpers';
import { isMatch2Available } from 'src/replicant/match2/match2.getters';

const ui = uiConfig.buruburu.menu;

export default class ShopMenu extends FlexBoxImageScaleView {
  constructor() {
    super({
      ...ui.menu,
      height: 900,
    });

    const { user } = StateObserver.getState();

    this.addEventButtons(this);

    // Default shops button
    this.addDefaultShopsButton(this);

    // Close hint label
    this.addCloseHintView(this);

    // Close on non button tap
    this.image.onInputStart = () => this.emit('Close');
  }

  /**
   * Event specific shop buttons
   */
  private addEventButtons(superview: View) {
    const now = StateObserver.now();
    const { user } = StateObserver.getState();

    if (!isDecorationGachaEventAvailable()) return;

    const currentGachaEvent = getCurrentGachaEvent(user, now);

    // If we don't have an event active
    if (!currentGachaEvent) return;

    const row = new FlexBoxView({
      superview,
      ...ui.menuRow,
      height: 190,
    });

    // Event specific gacha shop button
    row.addSubview(
      new GachaShopBanner({
        theme: currentGachaEvent.metadata!.theme,
        onSelect: () => this.emit('Close'),
      }),
    );
  }

  /**
   * Coins and gems shop buttons
   */
  private addDefaultShopsButton(superview: View) {
    const row = new FlexBoxView({
      superview,
      ...ui.menuRow,
      width: ui.menuRow.width,
      height: 170,
    });

    row.addSubview(
      new ShopActionButton({
        type: 'energy',
        onSelect: this.onSelect.bind(this),
        isDisabled: !isMatch2Available(StateObserver.getState().user),
      }),
    );

    row.addSubview(
      new ShopActionButton({
        type: 'gems',
        onSelect: this.onSelect.bind(this),
        isDisabled: isGemsButtonDisabled(),
      }),
    );
  }

  /**
   * Close menu hint message
   */
  private addCloseHintView(superview: View) {
    const row = new FlexBoxView({
      superview,
      ...ui.menuRow,
      width: 361,
      height: 41,
      flex: {
        marginTop: 23,
      },
    });

    const closeHint = new ImageView({
      superview: row,
      ...ui.closeHintBg,
      width: 361,
      height: 41,
      autoSize: false,
    });

    new LangBitmapFontTextView({
      superview: closeHint,
      ...ui.closeHintText,
      localeText: () => i18n('footer.tapToClose'),
      width: 361,
      height: 41,
    });
  }

  /**
   * Show shop menu
   */
  public fadeIn(superview: View) {
    animate(this)
      .clear()
      .now(() =>
        this.updateOpts({
          superview,
          visible: true,
          opacity: 0,
          offsetY: 100,
        }),
      )
      .then({ opacity: 1, offsetY: 0 }, animDuration, animate.easeOut);
  }

  /**
   * Hide shop menu
   */
  public fadeOut() {
    animate(this)
      .clear()
      .then({ opacity: 0, offsetY: 100 }, animDuration, animate.easeOut)
      .then(() => {
        this.removeFromSuperview();
      });
  }

  private onSelect = async (type: ShopActionType) => {
    sounds.sfx.play('ok');

    switch (type) {
      case 'coins':
        await openCoinsShopSequence('coins_shop_icon');

        this.emit('Close');
        return;

      case 'energy':
        await openEnergyShopSequence('energy_shop_icon');

        this.emit('Close');
        return;

      case 'gems':
        // If IAP enabled just show gems shop or alternative way to get gems if IAP disabled
        await openGemsShopSequence('gem_shop_icon');

        this.emit('Close');
        return;
    }
  };
}
